import { compact, orderBy, uniqBy } from 'lodash';

import { EntityDocumentViewer_DocumentFragment } from '@/modules/documents/components/HouseholdDocumentsViewer/graphql/EntityDocumentViewer.generated';
import { ENTITY_REFERENCE_DOCUMENT_TYPES } from '@/modules/documents/documents.constants';
import { DocumentDetails_DocumentFragment } from '@/modules/documents/graphql/DocumentDetails.generated';
import { EntityType } from '@/modules/entities/types/EntityType';
import { getEntityTypeFromEntityKind } from '@/modules/entities/utils/getEntityTypeFromEntityKind';
import {
  CreateEntityDocumentRelationshipInput,
  DeleteEntityDocumentRelationshipInput,
  DocumentType,
  EntityDocumentRelationshipRelationshipType,
  EntityKind,
} from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';

/**
 * Interface representing a Document from a relationship
 */
export interface DocumentFromRelationship {
  id: string;
  name?: string;
  displayType?: string;
  type?: string;
  file?: {
    id: string;
  };
}

/**
 * Interface representing the minimal structure of an EntityDocumentRelationship
 */
interface EntityDocumentRelationship {
  id: string;
  relationshipType: EntityDocumentRelationshipRelationshipType;
  entity?: {
    id: string;
    subtype?: { displayName?: string };
    extendedDisplayKind?: string;
  } | null;
  document?: DocumentFromRelationship | null;
  testamentaryEntity?: {
    id: string;
    displayName?: string;
  } | null;
}

/**
 * Interface representing the structure of an entity returned from relationships
 */
export interface EntityFromRelationship {
  id: string;
  subtype?: { displayName?: string };
  extendedDisplayKind?: string;
}

/**
 * Interface representing the structure of a testamentary entity returned from relationships
 */
export interface TestamentaryEntityFromRelationship {
  id: string;
  displayName?: string;
}

///////////////////////////////////////////////////////////
// Document Entity Relationships - Create/Update Inputs
///////////////////////////////////////////////////////////

/**
 * Creates entity document relationship inputs for multiple documents with the
 * given entityId.
 *
 * Sets the relationship type to SUGGESTION_SOURCE or MENTIONED based on the
 * entity type and document type combo.
 */
export function createEntityDocumentRelationshipsForMultipleDocuments({
  householdId,
  entityId,
  entityType,
  documents,
}: {
  householdId: string;
  entityId: string;
  entityType: EntityType;
  documents: { id: string; type: DocumentType }[];
}): CreateEntityDocumentRelationshipInput[] {
  if (!entityId) {
    return [];
  }

  return documents.map((document) => {
    const relationshipType = getEntityDocumentRelationshipType(
      entityType,
      document.type
    );

    return {
      householdID: householdId,
      documentID: document.id,
      entityID: entityId,
      relationshipType,
    };
  });
}

export function getEntityDocumentRelationshipType(
  entityType: EntityType,
  documentType: DocumentType
): EntityDocumentRelationshipRelationshipType {
  // Documents with types in this list is supported for suggestions, so we can
  // set the relationship type to SUGGESTION_SOURCE, with this entity.
  const supportedDocumentTypes =
    ENTITY_REFERENCE_DOCUMENT_TYPES[entityType] || [];
  return supportedDocumentTypes.includes(documentType)
    ? EntityDocumentRelationshipRelationshipType.SourceDocument
    : EntityDocumentRelationshipRelationshipType.Mentioned;
}

/**
 * When updating a document, create inputs for the entity-document relationships.
 * If initialDocument is provided, it will also calculate relationships that need to be deleted.
 */
export function getEntityDocumentRelationshipInputsForDocumentUpdate({
  householdId,
  documentId,
  sourceEntityIds = [],
  relatedEntityIds,
  relatedTestamentaryEntityIds,
  initialDocument,
}: {
  householdId: string;
  documentId: string;
  sourceEntityIds?: string[];
  relatedEntityIds: string[];
  relatedTestamentaryEntityIds: string[];
  initialDocument?: DocumentDetails_DocumentFragment;
}): {
  createInputs: CreateEntityDocumentRelationshipInput[];
  deleteInputs: DeleteEntityDocumentRelationshipInput[];
} {
  // These are the "associated" entities that are related to the document.
  const entityRelationships: CreateEntityDocumentRelationshipInput[] =
    relatedEntityIds.map((relatedEntityId) => ({
      householdID: householdId,
      documentID: documentId,
      entityID: relatedEntityId,
      relationshipType: EntityDocumentRelationshipRelationshipType.Mentioned,
    }));

  // If we're using the new improved UI, use sourceEntityIds for source relationships
  if (sourceEntityIds.length > 0) {
    sourceEntityIds.forEach((sourceEntityId) => {
      if (sourceEntityId) {
        entityRelationships.push({
          householdID: householdId,
          documentID: documentId,
          entityID: sourceEntityId,
          relationshipType:
            EntityDocumentRelationshipRelationshipType.SourceDocument,
        });
      }
    });
  }

  const testamentaryEntityRelationships: CreateEntityDocumentRelationshipInput[] =
    relatedTestamentaryEntityIds.map((relatedEntityId) => ({
      householdID: householdId,
      documentID: documentId,
      testamentaryEntityID: relatedEntityId,
      relationshipType: EntityDocumentRelationshipRelationshipType.Mentioned,
    }));

  const createInputs = [
    ...entityRelationships,
    ...testamentaryEntityRelationships,
  ];

  // Default to empty array for delete inputs
  let deleteInputs: DeleteEntityDocumentRelationshipInput[] = [];

  // Calculate relationships to delete if we have the initial document
  if (initialDocument) {
    // Get the IDs of the existing entities from the initial document
    let initialEntityIds: string[] = [];
    let initialTestamentaryEntityIds: string[] = [];
    let initialSourceEntityIds: string[] = [];

    const relationships =
      getNodes(initialDocument.entityDocumentRelationships) || [];

    // Get IDs from relationships
    relationships.forEach((rel) => {
      if (rel?.entity) {
        if (
          rel.relationshipType ===
          EntityDocumentRelationshipRelationshipType.SourceDocument
        ) {
          initialSourceEntityIds.push(rel.entity.id);
        } else {
          initialEntityIds.push(rel.entity.id);
        }
      } else if (rel?.testamentaryEntity) {
        initialTestamentaryEntityIds.push(rel.testamentaryEntity.id);
      }
    });

    // Get IDs for relationships that need to be deleted
    const entityIdsToDelete = initialEntityIds.filter(
      (id) => !relatedEntityIds.includes(id)
    );
    const testamentaryEntityIdsToDelete = initialTestamentaryEntityIds.filter(
      (id) => !relatedTestamentaryEntityIds.includes(id)
    );

    // Create inputs for relationships that need to be deleted
    deleteInputs = [
      ...entityIdsToDelete.map((id) => ({
        documentID: documentId,
        entityID: id,
      })),
      ...testamentaryEntityIdsToDelete.map((id) => ({
        documentID: documentId,
        testamentaryEntityID: id,
      })),
    ];

    // Delete source relationships that are no longer needed
    const sourceEntityIdsToDelete = initialSourceEntityIds.filter(
      (id) => !sourceEntityIds.includes(id)
    );

    deleteInputs.push(
      ...sourceEntityIdsToDelete.map((id) => ({
        documentID: documentId,
        entityID: id,
        relationshipType:
          EntityDocumentRelationshipRelationshipType.SourceDocument,
      }))
    );
  }

  return { createInputs, deleteInputs };
}

///////////////////////////////////////////////////////////
// Document Entity Relationships - DOCUMENTS
///////////////////////////////////////////////////////////

/**
 * Extracts documents from an array of EntityDocumentRelationship objects
 *
 * @param relationships Array of EntityDocumentRelationship objects
 * @param relationshipType Optional filter for relationship type (e.g., SourceDocument)
 * @returns Array of Document objects extracted from the relationships, with SOURCE_DOCUMENT types first
 */
export function getDocumentsFromRelationships<
  T extends DocumentFromRelationship = DocumentFromRelationship,
>(
  relationships: EntityDocumentRelationship[] | null | undefined,
  relationshipType?: EntityDocumentRelationshipRelationshipType
): T[] {
  if (!relationships) {
    return [];
  }

  // If filtering by specific relationship type, just filter and return
  if (relationshipType !== undefined) {
    return compact(
      relationships
        .filter(
          (rel) => rel?.document && rel.relationshipType === relationshipType
        )
        .map((rel) => rel.document as T)
    );
  }

  // When no relationship type is specified, sort SOURCE_DOCUMENT first
  const sourceDocuments: T[] = [];
  const otherDocuments: T[] = [];

  relationships.forEach((rel) => {
    if (!rel?.document) return;

    if (
      rel.relationshipType ===
      EntityDocumentRelationshipRelationshipType.SourceDocument
    ) {
      sourceDocuments.push(rel.document as T);
    } else {
      otherDocuments.push(rel.document as T);
    }
  });

  // Return with SOURCE_DOCUMENT relationships first, then others
  return uniqBy([...sourceDocuments, ...otherDocuments], 'id');
}

/**
 * Returns associated documents in the funding entity's legal-document categories.
 * A subtrust can inherit these documents, but not unrelated account statements
 * or instruments for a different entity category. Relationship type does not
 * establish whether a document governs the funding entity.
 */
export function getInheritableDocumentsFromRelationships<
  T extends DocumentFromRelationship = DocumentFromRelationship,
>(
  relationships: EntityDocumentRelationship[] | null | undefined,
  entityKind: EntityKind
): T[] {
  const documentTypes =
    ENTITY_REFERENCE_DOCUMENT_TYPES[getEntityTypeFromEntityKind(entityKind)];
  return getDocumentsFromRelationships<T>(relationships).filter((document) =>
    documentTypes.some((type) => type === document.type)
  );
}

///////////////////////////////////////////////////////////
// Document Entity Relationships - Get ENTITIES
///////////////////////////////////////////////////////////

/**
 * Gets entities from relationships, regardless of relationship type.
 *
 * @param relationships Array of EntityDocumentRelationship objects
 * @param relationshipType Optional filter for relationship type (e.g., SourceDocument)
 * @returns Array of EntityFromRelationship objects extracted from the relationships, with SOURCE_DOCUMENT types first
 */
export function getEntitiesFromRelationships(
  relationships: EntityDocumentRelationship[] | null | undefined,
  relationshipType?: EntityDocumentRelationshipRelationshipType
): EntityFromRelationship[] {
  if (!relationships) {
    return [];
  }

  if (relationshipType !== undefined) {
    return uniqBy(
      compact(
        relationships
          .filter(
            (rel) => rel?.entity && rel.relationshipType === relationshipType
          )
          .map((rel) => rel.entity)
      ),
      'id'
    );
  }

  // When no relationship type is specified, sort SOURCE_DOCUMENT first
  const sourceEntities: EntityFromRelationship[] = [];
  const otherEntities: EntityFromRelationship[] = [];

  relationships.forEach((rel) => {
    if (!rel?.entity) return;

    if (
      rel.relationshipType ===
      EntityDocumentRelationshipRelationshipType.SourceDocument
    ) {
      sourceEntities.push(rel.entity);
    } else {
      otherEntities.push(rel.entity);
    }
  });

  // Return with SOURCE_DOCUMENT relationships first, then others
  return uniqBy([...sourceEntities, ...otherEntities], 'id');
}

/**
 * Gets entities from relationships, excluding those with SOURCE_DOCUMENT relationship type
 *
 * @param relationships Array of EntityDocumentRelationship objects
 * @returns Array of entities that are not SOURCE_DOCUMENT relationships
 */
export function getNonSourceEntitiesFromRelationships(
  relationships: EntityDocumentRelationship[] | null | undefined
): EntityFromRelationship[] {
  if (!relationships) {
    return [];
  }

  return getEntitiesFromRelationships(
    relationships,
    EntityDocumentRelationshipRelationshipType.Mentioned
  );
}

///////////////////////////////////////////////////////////
// Document Entity Relationships - Get TESTAMENTARY ENTITIES
///////////////////////////////////////////////////////////

/**
 * Gets testamentary entities from relationships
 *
 * @param relationships Array of EntityDocumentRelationship objects
 * @returns Array of testamentary entities
 */
export function getTestamentaryEntitiesFromRelationships(
  relationships: EntityDocumentRelationship[] | null | undefined
): TestamentaryEntityFromRelationship[] {
  if (!relationships) {
    return [];
  }

  return compact(
    relationships
      .filter((rel) => rel?.testamentaryEntity)
      .map((rel) => rel.testamentaryEntity)
  );
}

///////////////////////////////////////////////////////////
// Document Entity Relationships - Order Documents
///////////////////////////////////////////////////////////

/**
 * Returns documents associated with the entity, newest first.
 * Document IDs break upload-date ties so query order does not affect the result.
 */
export function orderDocumentsByEntityRelationship(
  documents: EntityDocumentViewer_DocumentFragment[],
  entityId: string
): EntityDocumentViewer_DocumentFragment[] {
  if (!documents || !entityId) {
    return documents || [];
  }

  const documentsWithRelationship = documents.filter((document) =>
    getNodes(document.entityDocumentRelationships).some(
      (rel) => rel.entity?.id === entityId
    )
  );

  return orderBy(
    documentsWithRelationship,
    ['createdAt', 'id'],
    ['desc', 'asc']
  );
}
